'use client';
import React, { ReactNode, useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';
import { setUnitId } from '@/lib/slices/unit-slice';
import { setSubunitId } from '@/lib/slices/subunit-slice';
import { setActivityIndex } from '@/lib/slices/activity-slice';
import {
  setAnswerData,
  setQuizData,
  setQuizId,
  setQuizLockQuiz,
  setQuizName,
  setBlocksDownstream,
  setMinimumGradeThreshold,
  setRequiresMinimumGrade,
} from '@/lib/slices/quiz-slice';

import Button from '@/common/components/ui/Button';

import Request from '@/services/util/request-service';
import QuizBuilderService from '@/services/course/creation/quiz-builder-service';
import AdminCourseAccessService from '@/services/course/admin/admin-course-access-service';
import { ActivityData, CourseData, SubunitData, SubunitId, UnitId } from 'course-types';
import { role } from 'auth-types';
import { EditIcon } from '@/assets/icons/button';

interface Props {
  handleAddQuiz: () => void;
  handleEditQuiz: () => void;
}

export default function QuizListView({ handleAddQuiz, handleEditQuiz }: Props): React.JSX.Element {
  const store = useAppStore();
  const dispatch = useAppDispatch();

  const activeCourseData: CourseData = useAppSelector(
    () => store.getState().course.activeCourseData,
  );

  const userRole: role = useAppSelector(() => store.getState().isLoggedIn.role);

  const [quizData, setAllQuizData] = useState<
    Array<{
      unitId: UnitId;
      subunitId: SubunitId | null;
      subunitData: SubunitData | null;
      data: ActivityData;
    }>
  >(QuizBuilderService.getAllQuizzes(activeCourseData));

  const [isEditable, setIsEditable] = useState(false);

  useEffect(() => {
    setAllQuizData(QuizBuilderService.getAllQuizzes(activeCourseData));
  }, [activeCourseData.id]);

  const editQuiz = async (
    subunitId: SubunitId | null,
    unitId: UnitId | null,
    quizData: ActivityData,
  ): Promise<void> => {
    // Fetch quiz data
    const data = (await (await Request.get(`quiz/${quizData.id}`))?.json())?.quiz;

    // Close out if quiz does not exist / fetch correctly
    if (!data) {
      toast.error('Error fetching quiz data. Please refresh and try again.');
      return;
    }

    // Dispatch quiz info for editor
    dispatch(setUnitId(unitId != null ? unitId : undefined));
    dispatch(setSubunitId(subunitId != null ? subunitId : undefined));
    dispatch(setQuizId(quizData.id));
    dispatch(setQuizName(quizData.name));
    dispatch(setQuizData(data.quiz_data));
    dispatch(setAnswerData(data.answer_key));
    dispatch(setActivityIndex(quizData.index as number));
    dispatch(setQuizLockQuiz(data.lock_quiz ?? false));
    dispatch(setRequiresMinimumGrade(data.requires_minimum_grade));
    dispatch(setMinimumGradeThreshold(data.minimum_grade_threshold));
    dispatch(setBlocksDownstream(data.blocks_downstream_content));

    // Open editor
    handleEditQuiz();
    return;
  };

  useEffect(() => {
    setIsEditable(getEditableAccess());
  }, [activeCourseData, userRole]);

  const getEditableAccess = (): boolean => {
    return AdminCourseAccessService.getEditableAccess(userRole, activeCourseData);
  };

  const renderQuizList = (): ReactNode => {
    if (quizData.length < 1) {
      return <span>There are currently no quizzes for this course.</span>;
    }
    return (
      <table className='max-w-full border-collapse border border-border_gray_300 rounded-lg overflow-hidden bg-surface_gray_25'>
        <thead>
          <tr>
            <th className='border-b border-border_gray_300 px-4 py-2 text-left'>Quiz Name</th>
            <th className='border-b border-border_gray_300 px-4 py-2 text-left'>Subunit</th>
            <th className='border-b border-border_gray_300 px-4 py-2 text-left'></th>
          </tr>
        </thead>
        <tbody>
          {quizData.map((quiz, index) => (
            <tr key={index}>
              <td className='border-b border-border_gray_300 px-4 py-2'>{quiz.data.name}</td>
              {/* TODO:: make a better implementation for unit quizzes in the Quiz List View */}
              <td className='border-b border-border_gray_300 px-4 py-2'>
                {quiz.subunitData?.name ?? '-'}
              </td>
              <td className='border-b border-border_gray_300 px-4 py-2 text-center'>
                {isEditable && (
                  <Button
                    text={true}
                    className='flex flex-row items-center gap text-blue-500 hover:text-blue-700'
                    onClick={() => editQuiz(quiz.subunitId, quiz.unitId, quiz.data)}
                  >
                    <EditIcon className='w-4 mr-1' />
                    Edit
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div className='flex flex-col w-full'>
      {renderQuizList()}
      {isEditable && (
        <Button
          text={true}
          className='w-fit text-blue-500 hover:text-blue-700'
          onClick={handleAddQuiz}
        >
          + New Quiz
        </Button>
      )}
    </div>
  );
}
